import { useMutation } from '@apollo/client';
import axios from 'axios';
import { UserContext } from 'contexts/ConfigContext';
import { DEFAULT_WHITESPACE_PAGE_LIMIT } from 'contexts/whitespaceAnalysisContext.helpers';
import { FilterContextData } from 'contexts/FilterContext';
import { ppaUrls } from 'config/urls';
import { applyFilterMutation } from 'gqlQueries/filterQueries';
import { WhitespaceItem } from 'hooks/useWhitespaceAnalysisGridData';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    deleteTypeNameFromResponse,
    getConfig,
    getIntersectionOnApplyFilter,
    getScreenFilterDetails,
} from 'utils/helperMethods';

type WhitespaceGridRecord = {
    total_count?: number;
    data?: WhitespaceItem[];
};

type WhitespaceGridBucket = {
    id?: string;
    data?: WhitespaceGridRecord[];
};

type WhitespaceGridColumnGroup = {
    ex_id?: string;
    description?: string;
    data?: WhitespaceGridBucket[];
};

type WhitespaceGridResponse = {
    data?: WhitespaceGridColumnGroup[];
};

type CellKeyArgs = {
    bucketLabel: string;
    columnHeader: string;
};

type LoadMoreArgs = CellKeyArgs & {
    defaultItems: WhitespaceItem[];
    defaultMoreCount: number;
};

type UseWhitespaceCardPaginationParams = {
    gridRequestPayload: Record<string, unknown>;
    xAxisFilter: {
        dimension: string;
        attributeType: string;
        isAttribute: boolean;
    };
    childLimit?: number;
    onFilterApplied?: (payload: {
        filter_name: string;
        previous_value: string;
        selected_value: string;
        total_cell_count_before: number;
        total_cell_count_after: number;
        opportunity_cell_count_after: number;
    }) => void;
    getGridMetrics?: () => {
        total_cell_count: number;
        opportunity_cell_count: number;
    };
};

type AxisPagination = {
    limit?: number;
    offset?: number;
};

type RequestPagination = {
    child?: AxisPagination;
    column?: AxisPagination;
    row?: AxisPagination;
};

type WhitespaceGridRequestPayload = Record<string, unknown> & {
    pagination?: RequestPagination;
};

type CellPaginationState = {
    items: WhitespaceItem[];
    totalCount: number;
    isLoading: boolean;
    isExpanded: boolean;
    defaultVisibleCount: number;
};

type CellPaginationResult = {
    items: WhitespaceItem[];
    moreCount: number;
    isLoading: boolean;
    showCollapse: boolean;
};

type LoadMoreResult = {
    previousEntityCount: number;
    newEntityCount: number;
};

const stringifySafe = (value: unknown) => JSON.stringify(value ?? {});

const getCellKey = ({ bucketLabel, columnHeader }: CellKeyArgs) => `${bucketLabel}::${columnHeader}`;

const getRecordForCell = (
    response: WhitespaceGridResponse | undefined,
    bucketLabel: string,
    columnHeader: string,
): WhitespaceGridRecord | null => {
    const groups = response?.data ?? [];
    const matchedGroup = groups.find((group) => (group.description ?? group.ex_id ?? '') === columnHeader) ?? groups[0];
    const matchedBucket =
        (matchedGroup?.data ?? []).find((bucket) => (bucket.id ?? '') === bucketLabel) ?? matchedGroup?.data?.[0];
    return matchedBucket?.data?.[0] ?? null;
};

const mergeItems = (existingItems: WhitespaceItem[], nextItems: WhitespaceItem[]) => {
    const seen = new Set<string>();
    const merged: WhitespaceItem[] = [];

    [...existingItems, ...nextItems].forEach((item) => {
        const baseKey = `${item.ex_id ?? ''}|${item.dimension ?? ''}|${item.attribute_type ?? ''}|${item.description ?? ''}`;
        const key = baseKey === '|||' ? `${baseKey}-${merged.length}` : baseKey;
        if (seen.has(key)) return;
        seen.add(key);
        merged.push(item);
    });

    return merged;
};

const useWhitespaceCardPagination = ({
    gridRequestPayload,
    xAxisFilter,
    childLimit,
    onFilterApplied,
    getGridMetrics,
}: UseWhitespaceCardPaginationParams) => {
    const route = useLocation();
    const { appConfig } = useContext(UserContext);
    const { idForRestOfComponent } = useContext(FilterContextData);
    const globalFilterConfig = getConfig('globalFilter', appConfig);
    const { currentScreenFeatureName } = getScreenFilterDetails(route, globalFilterConfig);
    const [mutateApplyFilter] = useMutation(applyFilterMutation);
    const [cellPaginationMap, setCellPaginationMap] = useState<Record<string, CellPaginationState>>({});

    const parsedChildLimit = useMemo(() => (typeof childLimit === 'number' && childLimit > 0 ? childLimit : undefined), [childLimit]);

    useEffect(() => {
        setCellPaginationMap({});
    }, [gridRequestPayload, xAxisFilter.attributeType, xAxisFilter.dimension, xAxisFilter.isAttribute]);

    const getCellPaginationData = useCallback(
        ({ bucketLabel, columnHeader, defaultItems, defaultMoreCount }: LoadMoreArgs): CellPaginationResult => {
            const cellKey = getCellKey({ bucketLabel, columnHeader });
            const existingState = cellPaginationMap[cellKey];
            if (!existingState) {
                return {
                    items: defaultItems,
                    moreCount: defaultMoreCount,
                    isLoading: false,
                    showCollapse: false,
                };
            }

            const visibleItems = existingState.isExpanded
                ? existingState.items
                : existingState.items.slice(0, existingState.defaultVisibleCount);

            return {
                items: visibleItems,
                moreCount: Math.max(existingState.totalCount - visibleItems.length, 0),
                isLoading: existingState.isLoading,
                showCollapse: existingState.isExpanded && existingState.items.length > existingState.defaultVisibleCount,
            };
        },
        [cellPaginationMap],
    );

    const collapseCellItems = useCallback(({ bucketLabel, columnHeader }: CellKeyArgs) => {
        const cellKey = getCellKey({ bucketLabel, columnHeader });
        setCellPaginationMap((previous) => {
            const existingState = previous[cellKey];
            if (!existingState || !existingState.isExpanded) return previous;
            return {
                ...previous,
                [cellKey]: {
                    ...existingState,
                    isExpanded: false,
                },
            };
        });
    }, []);

    const loadMoreForCell = useCallback(
        async ({ bucketLabel, columnHeader, defaultItems, defaultMoreCount }: LoadMoreArgs): Promise<LoadMoreResult | undefined> => {
            const cellKey = getCellKey({ bucketLabel, columnHeader });
            const existingState = cellPaginationMap[cellKey];
            const existingItems = existingState?.items ?? defaultItems;
            const totalCount = existingState?.totalCount ?? defaultItems.length + defaultMoreCount;
            const defaultVisibleCount = existingState?.defaultVisibleCount ?? defaultItems.length;

            if (existingState?.isLoading) return;
            const previousEntityCount = existingState
                ? existingState.isExpanded
                    ? existingState.items.length
                    : existingState.defaultVisibleCount
                : defaultItems.length;

            if (existingState && !existingState.isExpanded && existingState.items.length > existingState.defaultVisibleCount) {
                setCellPaginationMap((previous) => ({
                    ...previous,
                    [cellKey]: {
                        ...existingState,
                        isExpanded: true,
                    },
                }));
                return { previousEntityCount, newEntityCount: existingState.items.length };
            }
            if (totalCount > 0 && existingItems.length >= totalCount) return;

            setCellPaginationMap((previous) => ({
                ...previous,
                [cellKey]: {
                    items: existingItems,
                    totalCount,
                    isLoading: true,
                    isExpanded: true,
                    defaultVisibleCount,
                },
            }));

            try {
                const metricsBefore = getGridMetrics?.() ?? { total_cell_count: 0, opportunity_cell_count: 0 };
                const clickedDimension = xAxisFilter.dimension || 'dim1';
                const clickedPayload = {
                    inclusion: [
                        {
                            attributeExId: columnHeader,
                            attributeDesc: columnHeader,
                            dimension: clickedDimension,
                            attributeType: xAxisFilter.attributeType,
                            isAttribute: xAxisFilter.isAttribute,
                        },
                    ],
                };

                const applyFilterVariables: Record<string, string> = {
                    jsonstring_dim1: '{}',
                    jsonstring_dim2: '{}',
                    jsonstring_dim3: '{}',
                    jsonstring_dim4: '{}',
                };
                applyFilterVariables[`jsonstring_${clickedDimension}`] = stringifySafe(clickedPayload);

                const applyFilterResponse = await mutateApplyFilter({
                    variables: {
                        ...applyFilterVariables,
                        featureName: currentScreenFeatureName,
                    },
                });

                const resolvedFilterResponse = { ...(applyFilterResponse.data ?? {}) };
                deleteTypeNameFromResponse(resolvedFilterResponse);
                const intersectedFilters = getIntersectionOnApplyFilter(idForRestOfComponent, resolvedFilterResponse);

                const nextGridPayload: WhitespaceGridRequestPayload = { ...gridRequestPayload };
                const userInputs = Array.isArray(nextGridPayload.user_inputs)
                    ? [...(nextGridPayload.user_inputs as Array<{ input_id?: string; input_val?: unknown }>)]
                    : [];
                nextGridPayload.filters = {
                    ...(typeof nextGridPayload.filters === 'object' && nextGridPayload.filters ? (nextGridPayload.filters as object) : {}),
                    dim1_id_list: intersectedFilters?.dim1?.ids ?? [],
                    dim2_id_list: intersectedFilters?.dim2?.ids ?? [],
                };
                nextGridPayload.user_inputs = [...userInputs.filter((input) => input?.input_id !== 'bucket'), { input_id: 'bucket', input_val: bucketLabel }];
                const existingPagination = nextGridPayload.pagination ?? {};
                nextGridPayload.pagination = {
                    ...existingPagination,
                    child: {
                        limit: parsedChildLimit ?? existingPagination.child?.limit ?? DEFAULT_WHITESPACE_PAGE_LIMIT,
                        offset: existingItems.length,
                    },
                };

                const gridResponse = await axios.post<WhitespaceGridResponse>(ppaUrls.getWhitespaceAnalysisGridUrl, nextGridPayload);
                const targetRecord = getRecordForCell(gridResponse.data, bucketLabel, columnHeader);
                const newItems = targetRecord?.data ?? [];
                const mergedItems = mergeItems(existingItems, newItems);
                const updatedTotalCount = Number(targetRecord?.total_count ?? totalCount);

                setCellPaginationMap((previous) => ({
                    ...previous,
                    [cellKey]: {
                        items: mergedItems,
                        totalCount: Number.isFinite(updatedTotalCount) ? updatedTotalCount : mergedItems.length,
                        isLoading: false,
                        isExpanded: true,
                        defaultVisibleCount,
                    },
                }));

                const metricsAfter = getGridMetrics?.() ?? metricsBefore;
                onFilterApplied?.({
                    filter_name: xAxisFilter.attributeType || 'x_axis',
                    previous_value: '-',
                    selected_value: columnHeader,
                    total_cell_count_before: metricsBefore.total_cell_count,
                    total_cell_count_after: metricsAfter.total_cell_count,
                    opportunity_cell_count_after: metricsAfter.opportunity_cell_count,
                });
                return { previousEntityCount, newEntityCount: mergedItems.length };
            } catch (_error) {
                setCellPaginationMap((previous) => ({
                    ...previous,
                    [cellKey]: {
                        items: existingItems,
                        totalCount,
                        isLoading: false,
                        isExpanded: true,
                        defaultVisibleCount,
                    },
                }));
            }
        },
        [
            cellPaginationMap,
            currentScreenFeatureName,
            gridRequestPayload,
            idForRestOfComponent,
            mutateApplyFilter,
            parsedChildLimit,
            xAxisFilter.attributeType,
            xAxisFilter.dimension,
            xAxisFilter.isAttribute,
            onFilterApplied,
            getGridMetrics,
        ],
    );

    return {
        getCellPaginationData,
        loadMoreForCell,
        collapseCellItems,
    };
};

export default useWhitespaceCardPagination;
